import { HeartbeatStorage } from "./window-heartbeat-storage";
import { v4 } from "uuid";

export const closed: WindowStatusType = "Closed";
export const unexpectedlyClosed: WindowStatusType = "UnexpectedlyClosed";
export const open: WindowStatusType = "Open";
export const windowHeartbeatPrefix = "wcbHB";
export const windowHeartbeatMasterKey = "wcbMasterKey";
export const windowHeartbeatKeys = "wcbKeys";
const heartbeatInterval = 10000;
const deadWindowTime = 30000;

let windowsHeartbeatService: WindowHeartbeatService = undefined;
export function getInstance(): WindowHeartbeatService {
  if (!windowsHeartbeatService) {
    windowsHeartbeatService = new WindowHeartbeatServiceImpl(window, window.localStorage);
  }
  return windowsHeartbeatService;
}

export interface WindowHeartbeatService {
  /** Provides an array of currently active service desk windows by id */
  getWindows(): WindowStatus[];

  /** The current window id */
  getCurrentWindowId(): string;

  /** registers a callback for updates on window statuses */
  registerStatusCallback(callback: (windowStatus: WindowStatus[]) => void): void;

  /** shuts down this window gracefully */
  shutdownThisWindowGracefully(): void;
}

export type WindowStatusType =
  /** The window was closed (gracefully) since the last heartbeat cycle */
  | "Closed"

  /** The window was closed unexpectedly since the last heartbeat cycle */
  | "UnexpectedlyClosed"

  /** The window continues to be open since the last heartbeat cycle */
  | "Open";

export interface WindowStatus {
  status: WindowStatusType;
  windowId: string;
}

export class WindowHeartbeatServiceImpl implements WindowHeartbeatService {
  private heartbeatRegex = new RegExp(`${windowHeartbeatPrefix}_(.*)`);
  private previousWindowStatus = new Map<string, WindowStatusType>();
  private statusCallbacks: any[] = [];
  private windowId: string;
  private windowHeartbeatKey: string;
  heartBeatStorage: HeartbeatStorage;

  constructor(private window: Window, private storage: Storage) {
    // We should never heartbeat within a iframe implicitly
    if (!(window as any).forceEnableHeartbeat && window != window.top) {
      return;
    }

    // there should only be a single active heartbeat per window be very careful that this is the case
    if ((window as any).heartbeatIdentifier) {
      this.windowId = (window as any).heartbeatIdentifier;
      return;
    }

    this.windowId = v4();
    (window as any).heartbeatIdentifier = this.windowId;
    window.setInterval(this.triggerHeartbeatAndUpdateStatus.bind(this), heartbeatInterval);
    this.heartBeatStorage = HeartbeatStorage.getInstance();
  }

  private cleanUpEntries(keys: Set<string>) {
    keys.forEach((key) => {
      this.storage.removeItem(key);
    });
  }

  private getWindowHeartbeatKey(): string {
    if (!this.windowHeartbeatKey) {
      let key = `${windowHeartbeatPrefix}_${this.windowId}`;
      this.heartBeatStorage.trackStorageItem(key, this.windowId);
      this.windowHeartbeatKey = key;
      return key;
    }
    return this.windowHeartbeatKey;
  }

  public getCurrentWindowId(): string {
    return this.windowId;
  }

  public shutdownThisWindowGracefully() {
    this.storage.removeItem(this.getWindowHeartbeatKey());
  }

  public getWindows(): WindowStatus[] {
    // if we haven't yet recorded any window information force a heartbeat and window status update
    if (!this.previousWindowStatus.size) {
      this.triggerHeartbeatAndUpdateStatus();
    }
    return this.previousWindowStatusToArray();
  }

  private previousWindowStatusToArray() {
    let windowStatus: WindowStatus[] = [];
    this.previousWindowStatus.forEach((status, windowId) => {
      windowStatus.push({ windowId: windowId, status: status });
    });
    return windowStatus;
  }

  public registerStatusCallback(callback: (windowStatus: WindowStatus[]) => void): void {
    if (callback) {
      this.statusCallbacks.push(callback);
    }
  }
  private triggerHeartbeatAndUpdateStatus() {
    let time = new Date().getTime();
    let timestamp = time.toString();
    this.heartBeatStorage.setItem(this.getWindowHeartbeatKey(), timestamp, this.windowId);

    let activeWindowIds = new Set<string>();
    let abandonedWindowIds = new Set<string>();
    let abandonedHeartbeatsKeys = new Set<string>();

    let masterWcbKey = this.heartBeatStorage.getItem(windowHeartbeatMasterKey);

    if (masterWcbKey == undefined) {
      masterWcbKey = this.windowId;
      this.heartBeatStorage.setItem(windowHeartbeatMasterKey, masterWcbKey, this.windowId);
    }

    if (masterWcbKey != this.windowId) {
      let wcbHbMasterKey = `${windowHeartbeatPrefix}_${masterWcbKey}`;
      let resetKey = false;
      try {
        let value = this.heartBeatStorage.getItem(wcbHbMasterKey);
        let windowTimestamp = parseInt(value);
        if (value == undefined || isNaN(windowTimestamp) || time - windowTimestamp > deadWindowTime) {
          resetKey = true;
        }
      } catch (error) {
        resetKey = true;
      }
      if (resetKey) {
        masterWcbKey = this.windowId;
        this.heartBeatStorage.setItem(windowHeartbeatMasterKey, masterWcbKey, this.windowId);
        this.heartBeatStorage.removeItem(wcbHbMasterKey);
      }
    }

    if (masterWcbKey == this.windowId) {
      // find and group heartbeats
      this.heartBeatStorage.getHeartbeatEntries().forEach((value: string, key: string) => {
        try {
          let [, windowId] = key.match(this.heartbeatRegex);
          let windowTimestamp = parseInt(value);
          if (time - windowTimestamp < deadWindowTime) {
            activeWindowIds.add(windowId);
          } else {
            abandonedWindowIds.add(windowId);
            abandonedHeartbeatsKeys.add(key);
          }
        } catch (error) {
          console.error(`Invalid heartbeat detected ${key}=>${value}, deleting`);
          this.storage.removeItem(key);
        }
      });
      this.heartBeatStorage.setItem(windowHeartbeatKeys, Array.from(activeWindowIds).join(","), this.windowId);
    } else {
      let keys = this.heartBeatStorage.getItem(windowHeartbeatKeys);
      if (keys) {
        keys.split(",").forEach((key) => {
          activeWindowIds.add(key);
        });
      }
    }
    let windowEvents = this.createWindowEvents(activeWindowIds, abandonedWindowIds);

    if ((<any>window).angular) {
      this.cleanUpEntries(abandonedHeartbeatsKeys);
    }

    this.previousWindowStatus = windowEvents;

    // Call any registered callbacks with window events
    let windowsForCallbacks = this.previousWindowStatusToArray();
    this.statusCallbacks.forEach((callback) => callback(windowsForCallbacks));
  }

  private createWindowEvents(activeWindowIds: Set<string>, abandonedHeartbeats: Set<string>) {
    let windowEvents = new Map<string, WindowStatusType>();

    this.previousWindowStatus.forEach((status, windowId) => {
      if (!activeWindowIds.has(windowId) && !abandonedHeartbeats.has(windowId)) {
        windowEvents.set(windowId, closed);
      }
    });

    abandonedHeartbeats.forEach((unexpectedlyClosedWindowId) => {
      windowEvents.set(unexpectedlyClosedWindowId, unexpectedlyClosed);
    });

    activeWindowIds.forEach((activeWindowId) => {
      if (!windowEvents.has(activeWindowId)) {
        windowEvents.set(activeWindowId, open);
      }
    });

    return windowEvents;
  }
}
